import { Button, ButtonLevel, ButtonSize, Popover, type PopoverProps } from '@vizit/atoms';
import { type IconProps, LightbulbIcon } from '@vizit/icons';
import { cx } from '@vizit/theme';
import type * as React from 'react';

interface InfoPopoverProps {
  /**
   * Accessible name of the icon button that opens the popover.
   */
  'aria-label': string;
  /**
   * The content of the popover, usually built from InfoPopover.Title, Body,
   * Section, List and Link.
   */
  children: React.ReactNode;
  /**
   * The icon shown on the button and at the top of the popover.
   *
   * @optional
   * @default LightbulbIcon
   */
  icon?: React.ComponentType<IconProps>;
  /**
   * Where the popover opens relative to the button.
   *
   * @optional
   * @default 'bottom'
   */
  placement?: PopoverProps['placement'];
  /**
   * Class name for the icon button, used to position it.
   *
   * @optional
   */
  className?: string;
}

/**
 * An icon button that opens a small popover of guidance: tips, short
 * explanations and links. Opens on click or Enter, closes on Escape or an
 * outside click, and hands focus back to the button. The content is free-form
 * so each screen writes its own.
 *
 * @example
 * <InfoPopover aria-label="Prompt tips">
 *   <InfoPopover.Title>Tips for your prompt</InfoPopover.Title>
 *   <InfoPopover.Body>Describe the scene you have in mind.</InfoPopover.Body>
 * </InfoPopover>
 */
function InfoPopover(props: InfoPopoverProps) {
  const { 'aria-label': ariaLabel, children, icon: Icon = LightbulbIcon, placement, className } = props;

  return (
    <Popover.Trigger>
      <Button
        aria-label={ariaLabel}
        level={ButtonLevel.Ghost}
        size={ButtonSize.Sm}
        iconStart={<Icon size={16} aria-hidden />}
        // Icon-only Sm buttons are 28px wide by 32px tall; square it so the icon sits evenly in corners.
        className={cx('w-16', className)}
      />
      <Popover placement={placement} className="w-[480px] max-w-[calc(100vw-32px)] overflow-y-auto shadow-lg">
        <Popover.Dialog className="flex flex-col gap-6 p-8">
          <div className="flex h-14 w-14 items-center justify-center border border-primary p-2">
            <Icon size={16} className="text-tertiary" aria-hidden />
          </div>
          {children}
        </Popover.Dialog>
      </Popover>
    </Popover.Trigger>
  );
}

export type { InfoPopoverProps };
export { InfoPopover };
